<template>
    <CqModal
        v-if="assignedDay"
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Dagtype toewijzen"
    >
        <InfoGrid>
            <InfoHeader>Dagtype</InfoHeader>
            <div>
                <CqRadiobutton class="radio" v-model="assignedDay.type" :value="rosterDayTypes.Normal" :disabled="isSaving" label="Weekdag" />
                <CqRadiobutton class="radio" v-model="assignedDay.type" :value="rosterDayTypes.Saturday" :disabled="isSaving" label="Zaterdag" />
                <CqRadiobutton class="radio" v-model="assignedDay.type" :value="rosterDayTypes.Sunday" :disabled="isSaving" label="Zondag" />
            </div>
        </InfoGrid>
        <CqButtonBar>
            <CqButton :disabled="isSaving" label="Annuleren" @click="closeModal" />
            <CqButton dangerous :disabled="!assignedDay.id || isSaving" label="Verwijderen" @click="remove" />
            <CqButton primary :disabled="(assignedDay.type === originalType && !isNonWeekLengthStamp) || isSaving" label="Bevestigen" @click="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal, CqRadiobutton} from '@cquential/ui';
    import {RosterDayType, ApiClient, PersonalStamp, PersonalStampAssignedDay, AssignedDay} from '@cquential/api';
    import Notify from '@/components/modals/Notify.vue';
    import Dates from '@cquential/date-utils';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqRadiobutton,
            InfoHeader,
            InfoGrid,
        },
        emits: [
            'update:assignedDay',
            'delete:assignedDay',
            'update:stampAssignedDay',
            'delete:stampAssignedDay',
        ],
    })
    export default class AssignedDaysModal extends Vue {
        private static instance: AssignedDaysModal | null;

        public assignedDay: AssignedDay | null = null;
        public originalType: RosterDayType | null = null;
        public show: boolean = false;
        public isInStampPhase: boolean = false;
        public stamp: PersonalStamp | null = null;
        public isSaving: boolean = false;
        public rosterDayTypes: typeof RosterDayType = RosterDayType;

        public mounted(): void {
            AssignedDaysModal.instance = this;
        }

        public beforeUnmount(): void {
            AssignedDaysModal.instance = null;
        }

        public get isDefaultAssignedDay(): boolean {
            if (!this.assignedDay) {
                return false;
            }

            if (this.isNonWeekLengthStamp) {
                // if the stamp does not have an iteration length of n * 7, you can never delete the assigned day (it will occur on every day in the week in the future [or past]).
                return false;
            }

            const currentDay: number = Dates.Utils.getDay(this.assignedDay.date);

            return (currentDay == Dates.Utils.SUNDAY && this.assignedDay.type === RosterDayType.Sunday)
                || (currentDay == Dates.Utils.SATURDAY && this.assignedDay.type === RosterDayType.Saturday)
                || (!(currentDay == Dates.Utils.SATURDAY || currentDay == Dates.Utils.SUNDAY) && this.assignedDay.type == RosterDayType.Normal);
        }

        public get isNonWeekLengthStamp(): boolean {
            return this.isInStampPhase && this.stamp!.iterationLength % 7 !== 0;
        }

        private remove(): void {
            if (!this.assignedDay || this.isSaving) {
                return;
            }

            this.isSaving = true;

            this.deleteAssignedDay().then((): void => {
                this.originalType = this.assignedDay!.type;
                this.closeModal();
            }).catch((e: Error): void => {
                console.warn(e);
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Er ging iets verkeerd. Probeer het opnieuw.', type: 'error'});
            }).finally((): void => {
                this.isSaving = false;
            });
        }

        private save(): void {
            if (!this.assignedDay || this.isSaving) {
                return;
            }

            this.isSaving = true;

            if (this.isDefaultAssignedDay) {
                this.remove();

                return;
            }

            this.saveAssignedDay().then((): void => {
                this.originalType = this.assignedDay!.type;
                this.closeModal();
            }).catch((): void => {
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Er ging iets verkeerd. Probeer het opnieuw.', type: 'error'});
            }).finally((): void => {
                this.isSaving = false;
            });
        }

        private deleteAssignedDay(): Promise<void> {
            if (!this.assignedDay || !this.assignedDay.id) {
                return Promise.resolve();
            }

            if (this.isInStampPhase) {
                const stampAssignedDay: PersonalStampAssignedDay = {
                    id: this.assignedDay.id,
                    stampId: this.stamp!.id!,
                    dayIndex: Dates.Utils.diffInDays(this.assignedDay.date, this.stamp!.beginDate) % this.stamp!.iterationLength,
                    type: this.assignedDay.type,
                };

                return ApiClient.instance.deleteStampAssignedDay(stampAssignedDay).then((): void => {
                    this.$emit('delete:stampAssignedDay', this.assignedDay!.resourceId, stampAssignedDay);
                });
            }

            return ApiClient.instance.deleteAssignedDay(this.assignedDay).then((): void => {
                this.$emit('delete:assignedDay', this.assignedDay!);
            });
        }

        private saveAssignedDay(): Promise<void> {
            if (!this.assignedDay) {
                return Promise.reject();
            }

            if (this.isInStampPhase) {
                const stampAssignedDay: PersonalStampAssignedDay = {
                    stampId: this.stamp!.id!,
                    dayIndex: Dates.Utils.diffInDays(this.assignedDay.date, this.stamp!.beginDate) % this.stamp!.iterationLength,
                    type: this.assignedDay!.type,
                };

                if (this.assignedDay.id) {
                    stampAssignedDay.id = this.assignedDay.id;
                }

                return ApiClient.instance.saveStampAssignedDay(stampAssignedDay).then((assignedId: number): void => {
                    this.assignedDay!.id = assignedId;
                    stampAssignedDay.id = assignedId;
                    this.$emit('update:stampAssignedDay', this.assignedDay!.resourceId, stampAssignedDay);
                });
            }

            return ApiClient.instance.saveAssignedDay(this.assignedDay).then((assignedId: number): void => {
                this.assignedDay!.id = assignedId;
                this.$emit('update:assignedDay', this.assignedDay!);
            });
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(openAssignedDay: AssignedDay, openIsInStampPhase: boolean, openStamp: PersonalStamp | null): void {
            this.instance!.assignedDay = openAssignedDay;
            this.instance!.originalType = openAssignedDay.type;
            this.instance!.isInStampPhase = openIsInStampPhase;
            this.instance!.stamp = openStamp;
            this.instance!.show = true;
        }
    }
</script>

<style lang="scss" scoped>
    .radio + .radio {
        margin-top: 0.25rem;
    }
</style>
